import * as ko from "knockout";
import * as localisationHelper from "localisationHelper";

// Components
import "Content/ts/Shared/Inputs/inputs-base";
import "Content/ts/Shared/Address/country-selector";
import "Content/ts/Shared/Inputs/TextButtonGroups/input-text-button-group";
import "Content/ts/Shared/BootstrapAlerts/AlertValidation/alert-validation";
import "Content/ts/Shared/BootstrapAlerts/AlertDanger/alert-danger";
import "Content/ts/Shared/BootstrapAlerts/AlertWarning/alert-warning";
import "@legend/web-components/input";

// Services
import { PostCodeLookupService } from "Content/ts/Shared/Services/PostCode/postcodeLookupService";
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";
import { ContactFieldService } from 'Content/ts/Join/Services/contactFieldService';
import { AddressServiceInstance } from "@legend/service/address";

import { AddressDetails } from 'Content/ts/Models/Join/ContactDetails';
import { SystemConfigurationModel } from "Content/ts/Models/Shared/SystemConfigurationModel";
import { PostCodeLookupAddress } from 'Content/ts/Models/Joining/PostCodeLookupAddress';
import { PostcodeLookupConfiguration } from "@legend/service/external/address";

export const Deps = {
    LocalisationHelper: localisationHelper,
    PostCodeLookupService: PostCodeLookupService,
    ContactFieldService: ContactFieldService
};

export interface IAddressParams {
    AddressDetails: AddressDetails;
    ValidationFields: KnockoutObservable<any>[];
    FieldMapping: any;
    ManualEntry: KnockoutObservable<boolean>;
};

export default class InputAddress {
    contactFieldMapping: any;

    customMessages: {
        AddressLine1Label: string;
        AddressLine2Label: string;
        CityLabel: string;
        CountyLabel: string;
        PostcodeLabel: string;
        SearchPostcodeScreenReaderText: string;
        FindAddressButtonText: string;
        SelectAddressLabel: string;
        DefaultPostcodeErrorMessage: string;
        PostcodeMinLength: string;
        EnterAddressManually: string
        AddressNotFound: string;
        SelectedAddressRequired: string;
        HouseNameOrNumber: string;
    };

    addressDetails: AddressDetails;

    selectedAddress: KnockoutObservable<PostCodeLookupAddress>;
    postcodeLookupInProgress: KnockoutObservable<boolean>;
    postcodeLookupResults: KnockoutObservableArray<any>;
    postCodeLookupEnabled: KnockoutObservable<boolean>;
    postcodeLookupFailed: KnockoutObservable<boolean>;
    showAddressEntryForm: KnockoutObservable<boolean>;
    validationFields: KnockoutObservable<any>[];
    minPostcodeLength: number;
    noAddressFound: KnockoutObservable<boolean>;
    manualEntry: KnockoutObservable<boolean>;

    enterAddressManually: KnockoutComputed<boolean>;
    showSelectedAddress: KnockoutComputed<boolean>;

    postcodeLookupConfiguration: KnockoutObservable<PostcodeLookupConfiguration>;
    houseNameOrNumber: KnockoutObservable<string>;

    constructor(params: IAddressParams) {
        this.addressDetails = params.AddressDetails || new AddressDetails();
        this.contactFieldMapping = params.FieldMapping;
        this.manualEntry = params.ManualEntry === undefined ? ko.observable(false) : params.ManualEntry;

        this.selectedAddress = ko.observable(null);
        this.postcodeLookupInProgress = ko.observable(false);
        this.postcodeLookupResults = ko.observableArray([]);
        this.postCodeLookupEnabled = ko.observable(false);
        this.showAddressEntryForm = ko.observable(false);
        this.postcodeLookupConfiguration = ko.observable({
            HouseNameOrNumberRequired: false
        });
        this.houseNameOrNumber = ko.observable("");

        this.validationFields = params.ValidationFields || [];
        this.minPostcodeLength = 5;
        this.noAddressFound = ko.observable(false);
        this.postcodeLookupFailed = ko.observable(false);

        Deps.PostCodeLookupService.PostcodeLookupEnabled()
            .then((response: SystemConfigurationModel) => {
                this.postCodeLookupEnabled(JSON.parse(response.SettingValue));
            });

        AddressServiceInstance.getPostcodeLookupConfiguration().then((response) => {
            this.postcodeLookupConfiguration(response);

            if (this.postcodeLookupConfiguration().HouseNameOrNumberRequired) {
                this.houseNameOrNumber.extend({
                    required: {
                        params: true,
                        message: localisationHelper.getString("GenericResources", "Address_HouseNameOrNumberRequiredLookupValidation")
                    }
                });
            }
        });

        this.retrieveCustomMessages();
        this.setupSubscriptions();
        this.setupComputeds();
        this.addValidation();
    }

    private setupComputeds(): void {
        this.enterAddressManually = ko.computed(() => {
            return this.manualEntry() || !this.postCodeLookupEnabled() || this.showAddressEntryForm();
        });

        this.showSelectedAddress = ko.computed(() => {
            return this.postcodeLookupResults().length > 0 && !this.enterAddressManually();
        });
    };

    private setupSubscriptions(): void {
        this.selectedAddress.subscribe((value) => {
            if (value) {
                this.addressDetails.AddressLine1(value.Address1);
                this.addressDetails.AddressLine2(value.Address2);
                this.addressDetails.City(value.City);
                this.addressDetails.County(value.County);
                this.addressDetails.Postcode(value.PostCode);
                this.showAddressEntryForm(true);
            }
        });
    };

    private retrieveCustomMessages(): void {
        // note these do not need to be required resources as the input text
        // component will automatically append the asterix
        this.customMessages = {
            AddressLine1Label: Deps.LocalisationHelper.getString("AccountResources", "Register_HouseStreet"),
            AddressLine2Label: Deps.LocalisationHelper.getString("AccountResources", "Register_AddressLineTwo"),
            CityLabel: Deps.LocalisationHelper.getString("AccountResources", "Register_City"),
            CountyLabel: Deps.LocalisationHelper.getString("AccountResources", "Register_County"),
            PostcodeLabel: Deps.LocalisationHelper.getString("AccountResources", "Register_Postcode"),
            SearchPostcodeScreenReaderText: Deps.LocalisationHelper.getString("AccessibilityResources", "ScreenReaderSearchForPostcode"),
            FindAddressButtonText: Deps.LocalisationHelper.getString("JoinResources", "FindAddress"),
            SelectAddressLabel: Deps.LocalisationHelper.getString("AccountResources", "Joining_SelectAddress"),
            DefaultPostcodeErrorMessage: Deps.LocalisationHelper.getString("ErrorMessageResources", "EditProfile_UnableToLocatePostcodeDetails"),
            PostcodeMinLength: Deps.LocalisationHelper.getString("ValidationMessageResources", "FieldHasMinLength").replace("{name}", "Postcode").replace("{length}", this.minPostcodeLength.toString()),
            EnterAddressManually: Deps.LocalisationHelper.getString("JoinResources", "EnterAddressManually"),
            AddressNotFound: Deps.LocalisationHelper.getString("PersonalProfileResources", "AddressNotFound"),
            SelectedAddressRequired: Deps.LocalisationHelper.getString("AccountResources", "Register_Validation_SelectedAddressRequired"),
            HouseNameOrNumber: Deps.LocalisationHelper.getString("GenericResources", "Address_HouseNameOrNumber")
        };
    };

    private addValidation(): void {
        ValidationService.AddValidationRule(this.addressDetails.Postcode, this.validationFields, {
            minLength: {
                params: this.minPostcodeLength,
                message: this.customMessages.PostcodeMinLength
            }
        });

        ValidationService.AddValidationRule(this.addressDetails.AddressLine1, this.validationFields, {
            maxLength: 50
        });

        ValidationService.AddValidationRule(this.selectedAddress, this.validationFields, {
            required: {
                params: true,
                message: this.customMessages.SelectedAddressRequired,
                onlyIf: () => {
                    return !this.enterAddressManually();
                }
            }
        });
    };

    private removeValidation() {
        ValidationService.RemoveValidationRule(this.addressDetails.Postcode, this.validationFields);
        ValidationService.RemoveValidationRule(this.addressDetails.AddressLine1, this.validationFields);
        ValidationService.RemoveValidationRule(this.selectedAddress, this.validationFields);
    };

    public dispose() {
        this.removeValidation();
    };

    public showManualInputForm(): boolean {
        return this.showAddressEntryForm() || this.manualEntry();
    }

    public showPostCodeLookupForm(): boolean {
        return this.postCodeLookupEnabled() && !this.manualEntry();
    }

    searchPostcode(): void {
        if (this.postcodeLookupConfiguration().HouseNameOrNumberRequired && !this.houseNameOrNumber.isValid()) {
            this.houseNameOrNumber.isModified(true);
            return;
        }

        this.showAddressEntryForm(false);
        this.postcodeLookupInProgress(true);
        this.postcodeLookupFailed(false);

        Deps.PostCodeLookupService.PostCodeSearch(this.houseNameOrNumber(), this.addressDetails.Postcode())
            .then((response: any) => {
                if (response.Success) {
                    this.postcodeLookupResults(response.Addresses);
                    if (!this.postcodeLookupResults().length) {
                        this.noAddressFound(true);
                        this.showAddressEntryForm(true);
                    } else {
                        this.noAddressFound(false);
                    }
                    ValidationService.HideValidationMessage(this.selectedAddress);
                } else {
                    this.postcodeLookupFailed(true);
                    this.showAddressEntryForm(true);
                }
            }).finally(() => {
                this.postcodeLookupInProgress(false);
            });;
    };

    manuallyEnterAddress(): void {
        this.showAddressEntryForm(true);
    };

    fieldVisible(element: HTMLElement): boolean {
        return this.contactFieldMapping ?
            Deps.ContactFieldService.getFieldProperties(element, this.contactFieldMapping).visible :
            this.enterAddressManually();
    }

    fieldRequired(element: HTMLElement): boolean {
        return this.contactFieldMapping ?
            Deps.ContactFieldService.getFieldProperties(element, this.contactFieldMapping).required :
            this.enterAddressManually();    // match previous functionality if no mapping provided
    }
}

const componentName = "input-address";
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: InputAddress,
    template: { require: `text!Content/ts/Shared/Inputs/Address/${componentName}.html` }
});